import {
  Button,
  Divider,
  EButtonColorVariant,
  ELinkButtonVariant,
  ETextStyleVariant,
  GeneralSocialColoredAppleIcon,
  GeneralSocialColoredFacebookIcon,
  GeneralSocialColoredGoogleIcon,
  LinkButton,
  Text,
} from '@outdoorsyco/bonfire';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TLocale } from '@/config/locales';
import { LOGIN_PAGE, SIGN_UP_PAGE } from '@/constants/urls';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import { TAccountType } from '@/services/types/auth/ILoginForm';
import { buildSocialAuthUrl } from '@/utility/buildSocialAuthUrl';
import { setItemInLocalStorage } from '@/utility/localstorage';
import { rememberSSOAuthOriginURL } from '@/utility/sso';

import ReCaptchaLegalNotice from './ReCaptchaLegalNotice';

interface IAuther {
  key: TAccountType;
  icon: JSX.Element;
  onClick: () => void;
  label: string;
}

const socialAuthers: IAuther[] = [
  {
    key: 'google',
    icon: <GeneralSocialColoredGoogleIcon fontSize={24} />,
    onClick: () => null,
    label: 'Google',
  },
  {
    key: 'facebook',
    icon: <GeneralSocialColoredFacebookIcon fontSize={24} />,
    onClick: () => null,
    label: 'Facebook',
  },
  {
    key: 'apple',
    icon: <GeneralSocialColoredAppleIcon fontSize={24} />,
    onClick: () => null,
    label: 'Apple',
  },
];

const onSocialAuth = (method: TAccountType, locale: TLocale) => {
  rememberSSOAuthOriginURL();
  setItemInLocalStorage('social_auth_method', method);
  window.location.href = buildSocialAuthUrl(method, locale);
};

interface IProps {
  className?: string;
  signup?: boolean | undefined;
  onSignup?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  onLogin?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  isPage?: boolean;
  didAcceptTerms?: boolean;
}

const AccountFormTemplate: React.FC<React.PropsWithChildren<IProps>> = ({
  children,
  className = '',
  onSignup,
  onLogin,
  signup,
  isPage,
  didAcceptTerms,
}) => {
  const intl = useIntl();
  const labelUsingSocialAuth = intl.formatMessage({
    defaultMessage: 'Or',
    id: '4wpUNc',
  });

  const linkLabelNoAccount = intl.formatMessage({
    defaultMessage: 'Don’t have an account yet? Join now',
    id: 'ljlE1a',
  });
  const linkLabelHaveAccount = intl.formatMessage({
    defaultMessage: 'Already have an account? Login',
    id: 'GzT4CO',
  });

  const { locale } = useLocaleSettings();

  const onSocialAuthClick = (event: React.MouseEvent<HTMLAnchorElement>, method: TAccountType) => {
    event.preventDefault();
    onSocialAuth(method, locale);
  };

  return (
    <div className={`w-full ${className}`}>
      {children}
      <section className="mt-5 text-center social-auth">
        <div className="relative flex items-center justify-center">
          <Divider className="absolute" />
          <div className="relative px-5 bg-white">
            <Text variant={ETextStyleVariant.MediumRegular} className="text-gray-700">
              {labelUsingSocialAuth}
            </Text>
          </div>
        </div>
        <div className="flex flex-col justify-center mt-5 gap-2">
          {socialAuthers.map((item: IAuther) => (
            <Button
              onClick={e => onSocialAuthClick(e, item.key)}
              key={item.key}
              disabled={signup && !didAcceptTerms}
              href=""
              label={
                <div className="flex items-center gap-2">
                  {item.icon}
                  {signup
                    ? intl.formatMessage(
                        {
                          defaultMessage: 'Join using {provider}',
                          id: 'afCr91',
                        },
                        { provider: item.label },
                      )
                    : intl.formatMessage(
                        {
                          defaultMessage: 'Log in using {provider}',
                          id: 'GSgP99',
                        },
                        { provider: item.label },
                      )}
                </div>
              }
              className="w-full"
              variant={EButtonColorVariant.Secondary}
            />
          ))}
        </div>
        {signup && !didAcceptTerms && (
          <Text variant={ETextStyleVariant.LegalRegular} className="mt-2 text-gray-50">
            <FormattedMessage
              defaultMessage="You must accept the Outdoorsy Terms of Service before signing up with a third-party account."
              id="5ek7Lz"
            />
          </Text>
        )}
      </section>
      <Divider className="mt-8" />
      <div
        className={`flex flex-col items-center justify-center px-8  mt-6  ${
          isPage ? 'mb-12' : ''
        }`}>
        {signup ? (
          <LinkButton
            variant={ELinkButtonVariant.Underline}
            label={linkLabelHaveAccount}
            onClick={onLogin}
            href={onLogin ? '' : LOGIN_PAGE}
          />
        ) : (
          <LinkButton
            variant={ELinkButtonVariant.Underline}
            label={linkLabelNoAccount}
            onClick={onSignup}
            href={onSignup ? '' : SIGN_UP_PAGE}
          />
        )}
      </div>
      <div className="mt-6">
        <ReCaptchaLegalNotice />
      </div>
    </div>
  );
};

export { AccountFormTemplate };
